import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { sfx } from '@/systems/sfx';

type ToastEntry = {
  container: Phaser.GameObjects.Container;
  tween: Phaser.Tweens.Tween;
};

export type ToastVariant = 'standard' | 'milestone' | 'anmeldelse' | 'kurs';

const VARIANT_FARGE: Record<ToastVariant, number> = {
  standard: COLORS.wine,
  milestone: 0xb8860b,
  anmeldelse: COLORS.forest,
  kurs: COLORS.ochre,
};

export type ToastValg = {
  variant?: ToastVariant;
  onClick?: () => void;
  varighetMs?: number;
};

export class ToastManager {
  private aktive: ToastEntry[] = [];

  constructor(private scene: Phaser.Scene) {}

  vis(melding: string, variantEllerValg: ToastVariant | ToastValg = 'standard'): void {
    const valg: ToastValg =
      typeof variantEllerValg === 'string' ? { variant: variantEllerValg } : variantEllerValg;
    const variant = valg.variant ?? 'standard';
    const varighetMs = valg.varighetMs ?? (valg.onClick ? 5000 : 3000);

    const container = this.scene.add.container(0, 0).setDepth(500);

    const padding = 28;
    const tekst = this.scene.add
      .text(padding + 4, padding, melding, {
        fontFamily: FONTS.body,
        fontSize: fontStr(26),
        color: '#1a1a1a',
        wordWrap: { width: 820 },
        lineSpacing: 4,
      })
      .setOrigin(0, 0);

    const boxW = Math.max(tekst.width + padding * 2 + 24, 420);
    const boxH = tekst.height + padding * 2;

    const bg = this.scene.add
      .rectangle(0, 0, boxW, boxH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(0);

    const borderFarge = VARIANT_FARGE[variant];
    const venstreBorder = this.scene.add
      .rectangle(0, 0, 4, boxH, borderFarge, 1)
      .setOrigin(0, 0);

    container.add([bg, venstreBorder, tekst]);

    if (valg.onClick) {
      const arrow = this.scene.add
        .text(boxW - 12, 8, '↗', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(24),
          color: '#862736',
          fontStyle: '700',
        })
        .setOrigin(1, 0);
      container.add(arrow);
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => bg.setFillStyle(0xf4ead5, 1));
      bg.on('pointerout', () => bg.setFillStyle(COLORS.white, 1));
      bg.on('pointerup', () => valg.onClick?.());
    }

    const x = (GAME_WIDTH - boxW) / 2;
    const slutY = GAME_HEIGHT - 80 - boxH - this.aktive.length * (boxH + 16);
    const startY = slutY + 50;
    container.setPosition(x, startY);
    container.setAlpha(0);

    sfx.play('notify');

    const tween = this.scene.tweens.add({
      targets: container,
      alpha: { from: 0, to: 1 },
      y: { from: startY, to: slutY },
      duration: 250,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        this.scene.time.delayedCall(varighetMs, () => {
          this.scene.tweens.add({
            targets: container,
            alpha: 0,
            duration: 500,
            onComplete: () => {
              container.destroy(true);
              this.aktive = this.aktive.filter((e) => e.container !== container);
            },
          });
        });
      },
    });

    this.aktive.push({ container, tween });
  }

  destroy(): void {
    for (const entry of this.aktive) {
      entry.tween.stop();
      entry.container.destroy(true);
    }
    this.aktive = [];
  }
}
